import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  getTextInputField,
  LandingLayout,
  LoadingSpinner,
} from '@frontend/howl-web-app/feature-main';
import React, { useEffect, useRef, useState } from 'react';
import { getProviders, signIn, signOut, useSession } from 'next-auth/react';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Navigation } from '../components/shared/navigation';
import { Button } from '@frontend/shared-ui-kit/button';
import { useForm } from '@mantine/form';
import { Icon } from '@frontend/shared-ui-kit/icon';
import { useActions } from '@frontend/howl-web-app/overmind';
import { useLocalStorage } from '@mantine/hooks';

const LoginPageV2 = () => {
  const { status: sessionStatus } = useSession();
  const { mobileLogout } = useActions().auth;

  const router = useRouter();
  const flags = useFlags();
  const formRef = useRef<HTMLFormElement>(null);

  enum View {
    CreatorLogin = 0,
    BrandLogin = 1,
    AdminLogin = 2,
  }

  const [currentView, setCurrentView] = useState<number>(View.CreatorLogin);
  const [loginValidation, setLoginValidation] = useState<string>('');
  const [isLoginOnly, setIsLoginOnly] = useState<boolean>(false);
  const [providers, setProviders] = useState<any>(null);
  const [submitting, setSubmitting] = useState<any>(false);
  const [bypassMobileLogout, setBypassMobileLogout] = useState<any>(false);

  const { status } = useSession();
  const [value, setValue] = useLocalStorage<string | null>({
    key: 'signinRedirectUrl',
    defaultValue: null,
  });

  // Redirects to homepage if user is signed in, otherwise redirects to signin page
  useEffect(() => {
    const { redirect, ...rest } = router.query;
    if (status === 'authenticated' && redirect !== 'undefined') {
      router.push('/');
    }
  }, [router, status]);

  useEffect(() => {
    const { redirect, ...rest } = router.query;
    if (redirect && typeof redirect === 'string') {
      const withQueryParams = Object.keys(rest).reduce((a, c) => {
        return (a += `&${c}=${rest[c]}`);
      }, redirect);
      setValue(withQueryParams);
    } else {
      setValue(null);
    }
  }, [router]);

  useEffect(() => {
    getProviders().then((providers) => setProviders(providers));
  }, []);

  // Check if creator, brand, or admin login based on query params
  useEffect(() => {
    const type = router.query.type;
    if (type) {
      if (type === 'creator') {
        setCurrentView(View.CreatorLogin);
      } else if (type === 'brand') {
        setCurrentView(View.BrandLogin);
      } else if (type === 'admin') {
        setCurrentView(View.AdminLogin);
      }
    }

    if (router.query.error) {
      handleError(router.query.error);
    }
  }, [router]);

  useEffect(() => {
    const loginOnly = router.query.login_only;
    if (typeof loginOnly === 'string' || loginOnly instanceof String) {
      if (loginOnly === 'true') {
        localStorage.setItem('mobileUser', 'true');
        setIsLoginOnly(true);

        if (sessionStatus === 'authenticated' && !bypassMobileLogout) {
          console.log('logging out due to mobile session');
          mobileLogout();
        }
      }
    }
  }, [router, sessionStatus]);

  useEffect(() => {
    if (localStorage.getItem('mobileUser')) setIsLoginOnly(true);
  }, []);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value: any) =>
        value.length <= 0 ? `Hmm... That doesn't look like an email.` : null,
      password: (value: any) =>
        value.length <= 0 ? `Hmm... That doesn't look like a password.` : null,
    },
  });

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setLoginValidation('');

    const { redirect, ...rest } = router.query;

    const validate = form.validate();

    if (!validate.hasErrors) {
      setSubmitting(true);
      setBypassMobileLogout(true);

      // Remove this item to force reshowing verification UI upon every login.
      localStorage.removeItem('sessionHideVerifyEmail');

      return performLogin({
        username: form.values.email,
        password: form.values.password,
      }).then((session: any) => {
        if (session && session.error) {
          handleError(session.error);
        } else if (session && session.ok) {
          // redirection is handled by apps/howl-web/src/pages/index.tsx, redirectAfterLogin()
        }
      });
    }
  };

  const performLogin = ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    return signOut({ redirect: false }).then(() =>
      signIn(providers['credentials'].id, {
        username: username,
        password: password,
        redirect: false,
        login_only: isLoginOnly,
        use_cognito_auth: flags.useCognitoAuth,
      }),
    );
  };

  const handleError = (error: any) => {
    setSubmitting(false);
    if (!error) return;

    // console.log('error: ', error);
    let error_code = 0;
    let error_msg = '';

    if (typeof error_msg === 'string' && error.includes(';')) {
      const error_parts = error.split(';');
      // console.log('error_parts: ', error_parts);
      if (error_parts.length > 1 && parseInt(error_parts[0])) {
        error_code = parseInt(error_parts[0]);
        error_msg = error_parts.slice(1).join();
      }
    } else {
      error_msg = error;
    }
    // console.log('error_code: ', error_code);
    // console.log('error_msg: ', error_msg);

    switch (error_code) {
      case 4305:
        router.push('/recover-password');
        break;
      case 7000:
        setLoginValidation(
          'The Howl platform is down for maintenance. ' +
            'Please try again in a few minutes.',
        );
        break;
      case 4001:
      case 4002:
      case 4301:
        setLoginValidation(
          `Your email/password combination doesn't match what we have.`,
        );
        break;
      case 4310:
        setLoginValidation(
          'Your account has not been approved to access the platform.',
        );
        break;
      default:
        if (
          error_msg === 'Invalid username/password combination.' ||
          error_msg === 'Password length must be at least 8 bytes.'
        ) {
          setLoginValidation(
            `Your email/password combination doesn't match what we have.`,
          );
        } else if (error_msg === 'Something went wrong') {
          setLoginValidation(`Unable to connect.`);
        } else if (error_msg === 'Network error') {
          setLoginValidation(
            `Unable to connect.  Make sure you're online or turn off adblocker.`,
          );
        } else if (error_msg === 'OAuthCallback') {
          setLoginValidation(
            'We are unable to complete login at this time through this sign-in method. Please try again later.',
          );
        } else if (typeof error_msg === 'string') {
          setLoginValidation(error_msg);
          console.log(`setLoginValidation('${error_msg}')`);
        }
    }
  };

  const getNavigationOptions = () => {
    return (
      !isLoginOnly && (
        <>
          {currentView === View.CreatorLogin && (
            <div
              className="font-medium text-violet-500 uppercase cursor-pointer hover:text-violet-300 transition"
              onClick={() => {
                router.push('/signin?type=brand');
                form.getInputProps('email').onChange('');
                form.getInputProps('password').onChange('');
              }}
              data-cy="brand-login-link"
              data-ref="brand-login-link"
            >
              Brand login
            </div>
          )}
          {currentView === View.BrandLogin && (
            <div
              className="font-medium text-violet-500 uppercase cursor-pointer hover:text-violet-300 transition"
              onClick={() => {
                router.push('/signin?type=creator');
                form.getInputProps('email').onChange('');
                form.getInputProps('password').onChange('');
              }}
              data-cy="creator-login-link"
              data-ref="creator-login-link"
            >
              Creator login
            </div>
          )}

          <div className="font-medium text-violet-500 uppercase ml-6 hover:text-violet-300 transition">
            <Link
              href={'/signup'}
              passHref
              data-cy={'sign-up-link'}
              data-ref={'sign-up-link'}
            >
              Sign up
            </Link>
          </div>
        </>
      )
    );
  };

  return (
    <div className={'h-full'}>
      <div className={'lg:block hidden'}>
        <img
          className={'absolute bottom-0 left-0 z-10 mb-10 h-[20vh] w-auto'}
          src={'/images/floating-island-half.png'}
        />
        <img
          className={
            'absolute z-10 bottom-0 left-0 ml-28 mb-32 h-[43vh] w-auto'
          }
          src={'/images/floating-island.png'}
        />
      </div>

      <Navigation options={getNavigationOptions()} />
      <div
        className={
          'max-w-[1600px] w-full lg:h-[85%] mx-auto flex lg:flex-row flex-col lg:px-0 md:px-28 px-4'
        }
      >
        <div className={'w-full lg:h-full flex lg:my-auto'}>
          <div className={'lg:w-fit w-full m-auto md:pt-0 pt-4 z-20'}>
            <div
              className={
                'sm:py-10 py-6 lg:px-10 px-6 bg-white rounded-2xl border border-gray-300'
              }
            >
              <div className="lg:w-[464px] align-middle">
                <div className={'text-center pb-4'}>
                  <div className={'flex flex-row text-center justify-center'}>
                    <div
                      className={classNames(
                        'lg:text-[40px] text-2xl text-violet-500 font-medium px-10 lg:leading-10 leading-7',
                        { 'pb-2': isLoginOnly },
                      )}
                    >
                      {currentView === View.CreatorLogin
                        ? 'Creator Login'
                        : currentView === View.BrandLogin
                        ? 'Brand Login'
                        : currentView === View.AdminLogin
                        ? 'Admin Login'
                        : null}
                    </div>
                  </div>
                  {!isLoginOnly && currentView !== View.AdminLogin && (
                    <div
                      className={
                        'text-gray-400 mb-2 lg:mt-3 mt-1 md:text-xl text-base'
                      }
                    >
                      {currentView === View.CreatorLogin ? (
                        <div className={'flex flex-row justify-center'}>
                          <div>Are you a brand?</div>
                          <span
                            className={classNames(
                              'text-violet-300 underline cursor-pointer pl-1',
                            )}
                            onClick={() => {
                              router.push('/signin?type=brand');
                              form.getInputProps('email').onChange('');
                              form.getInputProps('password').onChange('');
                            }}
                            data-cy="brand-login-link"
                            data-ref="brand-login-link"
                          >
                            Click here.
                          </span>
                        </div>
                      ) : (
                        <div className={'flex flex-row justify-center'}>
                          <div>Are you a creator?</div>
                          <span
                            className={classNames(
                              'text-violet-300 underline cursor-pointer pl-1',
                            )}
                            onClick={() => {
                              router.push('/signin?type=creator');
                              form.getInputProps('email').onChange('');
                              form.getInputProps('password').onChange('');
                            }}
                            data-cy="creator-login-link"
                            data-ref="creator-login-link"
                          >
                            Click here.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {currentView === View.CreatorLogin && (
                  <div>
                    <div className={'flex lg:flex-row flex-col'}>
                      <LoginButton
                        type={'Facebook'}
                        provider={'cognito_facebook'}
                        providers={providers}
                        isLoginOnly={true}
                      />
                      <LoginButton
                        type={'Google'}
                        provider={'cognito_google'}
                        providers={providers}
                        isLoginOnly={true}
                      />
                    </div>

                    <div className="relative flex py-5 -mt-3 items-center">
                      <div className="flex-grow border-t border-[1px] border-gray-200"></div>
                      <span className="flex-shrink mx-4 text-gray-400 uppercase">
                        Or log in with
                      </span>
                      <div className="flex-grow border-t border-[1px] border-gray-200"></div>
                    </div>
                  </div>
                )}

                {currentView !== View.AdminLogin ? (
                  <form
                    className=""
                    ref={formRef}
                    onSubmit={onSubmit}
                    noValidate
                  >
                    <div className={'flex flex-col justify-between'}>
                      <div>
                        {getTextInputField(
                          form.values.email.length,
                          form,
                          'Email Address',
                          'email',
                          'email',
                        )}
                      </div>

                      <div className={'mt-4'}>
                        {getTextInputField(
                          form.values.password.length,
                          form,
                          'Password',
                          'password',
                          'password',
                        )}
                      </div>

                      {loginValidation ? (
                        <div className="text-quasar-400 mt-2 py-1 w-full rounded-lg text-sm">
                          {loginValidation}
                        </div>
                      ) : null}

                      <div className={'text-violet-300 text-sm py-3'}>
                        {!isLoginOnly && (
                          <Link
                            href={'/recover-password'}
                            passHref
                            data-cy={'reset-password-link'}
                            data-ref={'reset-password-link'}
                          >
                            Forgot Password
                          </Link>
                        )}
                      </div>

                      <div
                        data-ref={'login-email-button'}
                        data-cy={'login-email-button'}
                      >
                        <Button
                          className={
                            '!w-full sm:mt-4 py-1 text-white !font-medium transition'
                          }
                          color="violet"
                          size={'xl'}
                          disabled={
                            form.values.email.length === 0 ||
                            form.values.password.length === 0 ||
                            submitting
                          }
                          type={'submit'}
                        >
                          {submitting ? (
                            <span>
                              <LoadingSpinner spinnerClassName="w-6 h-6"></LoadingSpinner>
                            </span>
                          ) : (
                            <div>Log In</div>
                          )}
                        </Button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className={'mt-6'}>
                    <LoginButton
                      type={'Okta'}
                      provider={'cognito_okta'}
                      providers={providers}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function LoginButton(props: any) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const iconName = 'sso' + props.type;

  return (
    <button
      className={
        'w-full flex flex-row text-center font-medium text-violet-500 border border-[#D1CDDE] lg:py-5 py-2 lg:mx-1 cursor-pointer rounded-lg mb-3 hover:bg-[#F3F1FE] hover:border-black transition'
      }
      onClick={(e) => {
        e.preventDefault();

        setIsLoading(true);
        signIn(props.providers[props.provider].id, {
          login_only: props.isLoginOnly,
        });
      }}
      data-cy={'login-' + props.type.toLowerCase() + '-button'}
      data-ref={'login-' + props.type.toLowerCase() + '-button'}
    >
      {isLoading ? (
        <div className={'m-auto lg:py-0 py-2'}>
          <LoadingSpinner spinnerClassName="w-6 h-6"></LoadingSpinner>
        </div>
      ) : (
        <div className={'mx-auto flex flex-row lg:w-auto w-full'}>
          <div className={'lg:w-8 w-16 my-auto'}>
            <Icon
              iconName={iconName}
              className="ml-2 w-4 h-4 stroke-2 lg:pl-0 pl-6"
            />
          </div>
          <div className={'my-auto lg:w-auto w-full lg:ml-1 ml-0 pr-1'}>
            {props.type}
          </div>
          <div className={'lg:w-0 w-16 h-10 lg:hidden block'}></div>
        </div>
      )}
    </button>
  );
}

LoginPageV2.Layout = LandingLayout;

export default LoginPageV2;
